<script setup>
import { ref, watch } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'

const props = defineProps({ vehicle: { type: Object, required: true } })
const emit = defineEmits(['close'])
const { t } = useI18n()

const form = useForm({
  requested_start_on: '',
  end_on: '',
  reason: ''
})

const preview = ref(null)
let timer = null

watch([() => form.requested_start_on, () => form.end_on], async ([s, e]) => {
  preview.value = null
  if (!s || !e) return
  clearTimeout(timer)
  timer = setTimeout(async () => {
    const r = await fetch(`/vehicles/${props.vehicle.id}/off_fleet/preview?start=${s}&end=${e}`, { credentials: 'same-origin' })
    if (r.ok) preview.value = await r.json()
  }, 250)
})

function submit() {
  form.transform(d => ({ block: d })).post(`/vehicles/${props.vehicle.id}/off_fleet`, {
    preserveScroll: true,
    onSuccess: () => emit('close')
  })
}
</script>

<template>
  <div class="overlay" @click.self="emit('close')">
    <div class="modal">
      <header class="modal__hdr">
        <h2>{{ t('supplier_portal.off_fleet.title') }}</h2>
        <button class="modal__x" @click="emit('close')">×</button>
      </header>
      <form @submit.prevent="submit" class="form">
        <label>{{ t('supplier_portal.off_fleet.start') }}<input type="date" v-model="form.requested_start_on" required /></label>
        <label>{{ t('supplier_portal.off_fleet.end') }}<input type="date" v-model="form.end_on" required /></label>
        <label>{{ t('supplier_portal.off_fleet.reason') }}<textarea v-model="form.reason" rows="2"></textarea></label>

        <div v-if="preview" class="preview" :class="{ 'preview--bad': preview.rejected }">
          <p v-if="preview.rejected">{{ preview.reason }}</p>
          <p v-else-if="preview.has_conflict">
            {{ t('supplier_portal.off_fleet.preview.conflict', { until: preview.conflict_ends_on, start: preview.effective_start_on, end: preview.end_on }) }}
          </p>
          <p v-else>
            {{ t('supplier_portal.off_fleet.preview.clean', { label: `${vehicle.make} ${vehicle.model}`, start: preview.effective_start_on, end: preview.end_on }) }}
          </p>
        </div>

        <div class="actions">
          <button type="button" class="btn" @click="emit('close')">{{ t('supplier_portal.off_fleet.cancel') }}</button>
          <button type="submit" class="btn btn--primary" :disabled="form.processing || preview?.rejected">
            {{ form.processing ? t('supplier_portal.off_fleet.confirm.loading') : t('supplier_portal.off_fleet.confirm.idle') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal {
  background: var(--surface);
  border-radius: var(--radius-lg, 12px);
  padding: 1.25rem;
  width: 100%;
  max-width: 28rem;
  max-height: 90vh;
  overflow-y: auto;
}
.modal__hdr {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.modal__hdr h2 {
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: var(--ink);
}
.modal__x {
  background: none;
  border: 0;
  font-size: 1.5rem;
  color: var(--muted);
  cursor: pointer;
}
.form {
  display: grid;
  gap: 0.875rem;
}
.form label {
  display: grid;
  gap: 0.3rem;
  font-size: 0.8125rem;
  color: var(--inkSoft);
}
.form input,
.form textarea {
  padding: 0.5rem 0.75rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-input, 8px);
  color: var(--ink);
  font: inherit;
}
.form input:focus,
.form textarea:focus {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.preview {
  padding: 0.65rem 0.85rem;
  background: var(--surfaceAlt);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--inkSoft);
}
.preview--bad {
  background: color-mix(in oklch, var(--danger) 12%, var(--surface));
  color: var(--danger);
}
.preview p {
  margin: 0;
}
.actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}
.btn {
  padding: 0.45rem 0.85rem;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  border-radius: var(--radius-button, 8px);
  cursor: pointer;
  font-weight: 500;
}
.btn--primary {
  background: var(--accent);
  color: var(--accentInk);
  border-color: var(--accent);
}
.btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
